import { BaseFilterComponent } from './types';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Filter } from '../../types';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { NumericRangeFilterOption } from '../options/types';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import _isNil from 'lodash/isNil';
import * as React from 'react';

interface NumericRangeProps
  extends NumericRangeFilterOption,
    BaseFilterComponent {}

export const useStyles = makeStyles((theme: Theme) => ({
  spacing: {
    marginBottom: theme.spacing(1),
  },
  textField: {
    '& input': {
      minWidth: 0,
    },
  },
}));

const messages = defineMessages({
  min: {
    id: 'lifeologyActivityForm.min',
    defaultMessage: '>= Min',
  },
  max: {
    id: 'lifeologyActivityForm.max',
    defaultMessage: '<= Max',
  },
});

export const NumericRange: React.FC<NumericRangeProps> = ({
  injectedProps: {
    buttonIntlLabel,
    defaultMaximumValue,
    defaultMinimumValue,
    id,
    label,
    maximumValue,
    minimumValue,
    searchKey,
    step,
    maxHelpMessage,
    minHelpMessage,
    maximumOption,
    minimumOption,
  },
  onFilterAdd,
  origin,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const [minEnteredValue, setMinEnteredValue] = React.useState<
    number | undefined
  >(
    !_isNil(defaultMinimumValue)
      ? parseFloat(String(defaultMinimumValue))
      : undefined,
  );
  const [maxEnteredValue, setMaxEnteredValue] = React.useState<
    number | undefined
  >(
    !_isNil(defaultMaximumValue)
      ? parseFloat(String(defaultMaximumValue))
      : undefined,
  );

  const handleMinKeyPress = (e: React.ChangeEvent<HTMLInputElement>) =>
    setMinEnteredValue(parseFloat(e.target.value));
  const handleMaxKeyPress = (e: React.ChangeEvent<HTMLInputElement>) =>
    setMaxEnteredValue(parseFloat(e.target.value));

  const handleSubmitClick = () => {
    if (onFilterAdd) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      if (!isNaN(minEnteredValue) || !isNaN(maxEnteredValue)) {
        const option: Filter = {
          id,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          origin,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          searchKey,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          friendlyName:
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            (!isNaN(minEnteredValue) &&
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              !isNaN(maxEnteredValue) &&
              `${minEnteredValue}-${maxEnteredValue}`) ||
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            (!isNaN(minEnteredValue) &&
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              isNaN(maxEnteredValue) &&
              `>= ${minEnteredValue}`) ||
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            (isNaN(minEnteredValue) &&
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              !isNaN(maxEnteredValue) &&
              `<= ${maxEnteredValue}`),
          searchValue:
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            (!isNaN(minEnteredValue) &&
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              !isNaN(maxEnteredValue) &&
              `${minEnteredValue}-${maxEnteredValue}`) ||
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            (!isNaN(minEnteredValue) &&
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              isNaN(maxEnteredValue) &&
              `${minEnteredValue}:gte`) ||
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            (isNaN(minEnteredValue) &&
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              !isNaN(maxEnteredValue) &&
              `${maxEnteredValue}:lte`),
        };
        onFilterAdd(option);

        setMinEnteredValue(parseFloat(String(defaultMinimumValue)));
        setMaxEnteredValue(parseFloat(String(defaultMaximumValue)));
      }
    }
  };

  return (
    <>
      {label && (
        <Text size="subbody" weight="bold" className={classes.spacing}>
          {formatMessage(label)}
        </Text>
      )}
      <FormBox direction="row" className={classes.spacing}>
        <TextField
          className={classes.textField}
          type="number"
          value={minEnteredValue}
          min={minimumValue}
          step={step}
          onChange={handleMinKeyPress}
          helpMessage={minHelpMessage && formatMessage(minHelpMessage)}
          aria-label={formatMessage(messages.min)}
          fullWidth
          placeholder={
            minimumOption?.placeholderIntlLabel
              ? formatMessage(minimumOption?.placeholderIntlLabel)
              : undefined
          }
        />
        <TextField
          className={classes.textField}
          type="number"
          value={maxEnteredValue}
          min={minEnteredValue}
          max={maximumValue}
          onChange={handleMaxKeyPress}
          helpMessage={maxHelpMessage && formatMessage(maxHelpMessage)}
          aria-label={formatMessage(messages.max)}
          fullWidth
          placeholder={
            maximumOption?.placeholderIntlLabel
              ? formatMessage(maximumOption?.placeholderIntlLabel)
              : undefined
          }
        />
      </FormBox>
      <Button
        variant="contained"
        onClick={handleSubmitClick}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        disabled={isNaN(minEnteredValue) && isNaN(maxEnteredValue)}
        fullWidth
      >
        {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
        {formatMessage(buttonIntlLabel)}
      </Button>
    </>
  );
};
